import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, Sparkles, ShieldCheck } from "lucide-react";

export default function Pricing() {
  const { t, user, refreshMe } = useApp();
  const [loadingKey, setLoadingKey] = useState(null);
  const currentTier = user?.subscription_tier || "free";

  const plans = [
    {
      key: "free", name: t("planFreeName"), price: "\u20ac0", period: "",
      features: [t("pitchesLifetime"), t("featSingle")],
      recommended: false,
    },
    {
      key: "pro", name: t("planProName"), price: "\u20ac49", period: t("perMonth"),
      features: [t("pitchesMonthly"), t("featSingle"), t("featBatch"), t("featPdf")],
      recommended: true,
    },
    {
      key: "agency", name: t("planAgencyName"), price: "\u20ac149", period: t("perMonth"),
      features: [t("pitchesUnlimited"), t("featBatch"), t("featPdf"), t("featCsv")],
      recommended: false,
    },
  ];

  const choose = async (key) => {
    if (key === currentTier) return;
    setLoadingKey(key);
    try {
      if (key === "free") {
        await api.post("/billing/cancel");
      } else {
        await api.post("/billing/subscribe", { tier: key });
      }
      await refreshMe();
      toast.success(t("planUpdated"));
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div>
      <div className="text-center">
        <h1 className="font-heading text-3xl font-semibold tracking-tight">{t("pricingTitle")}</h1>
        <p className="mt-2 text-muted-foreground">{t("pricingSubtitle")}</p>
      </div>

      <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
        {plans.map((p) => {
          const isCurrent = p.key === currentTier;
          return (
            <Card
              key={p.key}
              className={`card-lift relative flex flex-col p-6 ${p.recommended ? "border-primary ring-1 ring-primary" : ""}`}
              data-testid={`pricing-card-${p.key}`}
            >
              {p.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  {t("recommended")}
                </span>
              )}
              <h3 className="font-heading text-xl font-semibold">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-semibold">{p.price}</span>
                <span className="text-muted-foreground">{p.period}</span>
              </div>
              <ul className="mt-5 flex-1 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                className="mt-6 w-full"
                variant={isCurrent ? "secondary" : p.recommended ? "default" : "outline"}
                disabled={isCurrent || loadingKey === p.key}
                onClick={() => choose(p.key)}
                data-testid={`pricing-${p.key}-select-button`}
              >
                {isCurrent ? t("currentPlan") : loadingKey === p.key ? t("loading") : (p.key === "free" ? t("downgrade") : t("choosePlan"))}
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t("stripeTrust")}</span>
        <span className="flex items-center gap-2 text-xs"><Sparkles className="h-3.5 w-3.5" /> {t("stubNotice")}</span>
      </div>
    </div>
  );
}
